import { Component, EventEmitter, Output, ViewChild, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { DrawerGapFromTopComponent } from '@lib/components/drawer-gap-from-top/drawer-gap-from-top.component';
import { PopoverMenuComponent } from '@lib/components/popover-menu/popover-menu.component';
import { PopoverSelectDevicesComponent } from '@lib/components/popover-multi-select/object-types/devices/popover-select-devices.component';
import { Option, OptionGroup } from '@shared/models/option';
import { Device, DeviceType, PhysicalDeviceType } from '@shared/models/device';
import { FormulaBindingSource } from '@shared/models/formula-device';
import { DevicesService } from '@core/services/devices.service';
import { TileDataSource } from '../../../shared/models/tile-data-source.model';
import { TileId } from '@shared/models/tile';
import { CanvasMiniTileComponent } from '@features/canvas-tiles/editor/components/canvas-mini-tile/canvas-mini-tile.component';
import { FORMULA_TEMPLATES, FormulaTemplate, FormulaVariable } from '../../../shared/constants/formula-templates.constants';
import { CONVERSION_OPTIONS, DEFAULT_STALE_THRESHOLD_MINUTES, MULTIPLIER_OPTIONS } from '@shared/constants/tile-config.constants';
import { DevicePointSelectorComponent, PointSelection } from '../../../device-point-selector/device-point-selector.component';
import { SpinnerComponent } from '@lib/components/spinner/spinner.component';
import { NumericTileCreatorService } from '../../services/numeric-tile-creator.service';
import { ShadeToolsService } from '@core/services/shade-tools.service';
import { FormulaCustomInputComponent } from '../formula-custom-input/formula-custom-input.component';
import { FormulaVariablePillComponent } from '../formula-variable-pill/formula-variable-pill.component';
import { CUSTOM_FORMULA_ID, FormulaValidationResult } from '../../../shared/services/formula.service';
import { CustomFormulaState, DeviceSelection, EMPTY_CUSTOM_FORMULA, VariableUiState } from '../../../shared/models/numeric-tile-creator.model';
import { PillColor } from '../formula-variable-pill/formula-variable-pill.model';

@Component({
  selector: 'motn-numeric-tile-creator',
  templateUrl: './numeric-tile-creator.component.html',
  styleUrls: ['./numeric-tile-creator.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DrawerGapFromTopComponent,
    PopoverMenuComponent,
    PopoverSelectDevicesComponent,
    DevicePointSelectorComponent,
    CanvasMiniTileComponent,
    SpinnerComponent,
    FormulaCustomInputComponent,
    FormulaVariablePillComponent,
  ],
})
export class NumericTileCreatorComponent {
  constructor(
    private devicesService: DevicesService,
    private creatorService: NumericTileCreatorService,
    private shadeTools: ShadeToolsService,
  ) {}

  @ViewChild(FormulaCustomInputComponent) private formulaInput?: FormulaCustomInputComponent;

  @Output() closed$ = new EventEmitter<void>();
  @Output() created$ = new EventEmitter<TileId>();

  TileDataSource = TileDataSource;
  allowedDeviceTypes: PhysicalDeviceType[] = [DeviceType.BACnet, DeviceType.Modbus, DeviceType.LoRaWAN];
  formulaDeviceTypes: PhysicalDeviceType[] = [DeviceType.LoRaWAN];

  dataSource = signal<TileDataSource>(TileDataSource.Device);

  // Device flow
  device = signal<DeviceSelection>({ id: null, name: null, pointId: null, pointName: null, pointValue: null });
  deviceSelectorOpen = signal(false);

  // Formula flow
  formula = signal<FormulaTemplate | null>(null);
  bindings = signal<Record<string, Partial<FormulaBindingSource>>>({});
  variableUiState = signal<Record<string, VariableUiState>>({});
  activeSelectorKey = signal<string | null>(null);
  formulaDropdownOpen = signal(false);
  kebabOpenKey = signal<string | null>(null);

  // Custom formula flow
  isCustomFormula = signal(false);
  customFormula = signal<CustomFormulaState>(EMPTY_CUSTOM_FORMULA);

  // Point selector drawer
  pointSelectorDevice = signal<Device | null>(null);
  pointSelectorVariableKey = signal<string | null>(null);

  loading = signal(false);

  currentVariables = computed<FormulaVariable[]>(() => {
    if (this.isCustomFormula()) {
      return this.customFormula().variables.map(key => ({ key, label: key, description: '' }));
    }
    return this.formula()?.variables ?? [];
  });

  variableColors = computed<Record<string, PillColor>>(() => {
    const result: Record<string, PillColor> = {};
    for (const v of this.currentVariables()) {
      result[v.key] = this.isBindingComplete(v.key) ? 'green' : 'red';
    }
    return result;
  });

  canProceed = computed<boolean>(() => {
    if (this.dataSource() === TileDataSource.Device) {
      return !!(this.device().id && this.device().pointId);
    }
    if (this.isCustomFormula()) {
      if (!this.customFormula().valid) return false;
      if (this.customFormula().variables.length === 0) return false;
      return this.currentVariables().every(v => this.isBindingComplete(v.key));
    }
    if (!this.formula()) return false;
    return this.currentVariables().every(v => this.isBindingComplete(v.key));
  });

  conversionOptions = CONVERSION_OPTIONS;
  multiplierOptions = MULTIPLIER_OPTIONS;
  defaultStaleThreshold = DEFAULT_STALE_THRESHOLD_MINUTES;

  readonly deleteMenuOption: OptionGroup = new OptionGroup([{ id: 'delete', name: 'Delete' }]);

  readonly operators = ['+', '-', '*', '/', '^', '(', ')'] as const;
  readonly formulaFunctions: { label: string; text: string; cursorBack: number }[] = [
    { label: 'abs()', text: 'abs()', cursorBack: 1 },
    { label: 'ceil()', text: 'ceil()', cursorBack: 1 },
    { label: 'floor()', text: 'floor()', cursorBack: 1 },
    { label: 'log()', text: 'log()', cursorBack: 1 },
    { label: 'round()', text: 'round()', cursorBack: 1 },
  ];

  readonly formulaOptions: OptionGroup = new OptionGroup([
    ...FORMULA_TEMPLATES.map(f => ({ id: f.id, name: f.name })),
    { id: CUSTOM_FORMULA_ID, name: 'Create Custom Formula' },
  ]);

  deviceSelectorPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'start', originY: 'top',    overlayX: 'start', overlayY: 'bottom' },
  ];

  onDataSourceChange(value: TileDataSource) {
    this.dataSource.set(value);
    this.formula.set(null);
    this.isCustomFormula.set(false);
    this.resetDeviceState();
    this.resetFormulaState();
  }

  onDeviceSelected(option: Option) {
    this.device.set({ id: option.id, name: option.name, pointId: null, pointName: null, pointValue: null });
    this.deviceSelectorOpen.set(false);
  }

  selectPoint() {
    const device = this.getDevice(this.device().id);
    if (device) {
      this.pointSelectorDevice.set(device);
      this.pointSelectorVariableKey.set(null);
    }
  }

  onSelectFormula(option: Option) {
    this.resetFormulaState();
    if (option.id === CUSTOM_FORMULA_ID) {
      this.isCustomFormula.set(true);
      this.formula.set(null);
    } else {
      const entry = FORMULA_TEMPLATES.find(f => f.id === option.id);
      if (entry) {
        this.formula.set(entry);
      }
      this.isCustomFormula.set(false);
    }
    this.formulaDropdownOpen.set(false);
  }

  onCustomFormulaChanged(expression: string) {
    this.customFormula.update(s => ({ ...s, expression, validated: false, validationError: null }));
  }

  onVariableRenamed({ oldName, newName }: { oldName: string; newName: string }) {
    this.bindings.update(b => {
      const { [oldName]: oldBinding, ...rest } = b;
      return oldBinding ? { ...rest, [newName]: oldBinding } : rest;
    });
    this.variableUiState.update(s => {
      const { [oldName]: oldState, ...rest } = s;
      return oldState ? { ...rest, [newName]: oldState } : rest;
    });
  }

  onCustomFormulaVariablesChanged(variables: string[]) {
    const removed = this.customFormula().variables.filter(v => !variables.includes(v));

    if (removed.length) {
      const drop = <T>(rec: Record<string, T>) =>
        Object.fromEntries(Object.entries(rec).filter(([k]) => !removed.includes(k)));
      this.bindings.update(drop);
      this.variableUiState.update(drop);
    }

    this.customFormula.update(s => ({ ...s, variables }));
  }

  onCustomFormulaValidationResult({ valid, error }: FormulaValidationResult) {
    this.customFormula.update(s => ({ ...s, valid, validated: true, validationError: error ?? null }));
  }

  onVariableDeviceSelected(variableKey: string, option: Option) {
    this.bindings.update(b => ({ ...b, [variableKey]: { DeviceID: option.id } }));
    // Reset to just deviceName — clears any stale point data for this variable
    this.variableUiState.update(s => ({ ...s, [variableKey]: { deviceName: option.name } }));
    this.activeSelectorKey.set(null);
  }

  selectVariablePoint(variableKey: string) {
    const device = this.getDevice(this.bindings()[variableKey]?.DeviceID ?? null);
    if (device) {
      this.pointSelectorDevice.set(device);
      this.pointSelectorVariableKey.set(variableKey);
    }
  }

  onPointSelected(selection: PointSelection) {
    const key = this.pointSelectorVariableKey();
    if (key) {
      this.bindings.update(b => ({ ...b, [key]: { ...b[key], PointID: selection.pointId } }));
      this.variableUiState.update(s => ({ ...s, [key]: { ...s[key], pointName: selection.pointName } }));
      this.fetchVariablePointValue(key, selection.pointId);
    } else {
      this.device.update(d => ({ ...d, pointId: selection.pointId, pointName: selection.pointName }));
      this.fetchDevicePointValue(selection.pointId);
    }
    this.pointSelectorDevice.set(null);
    this.pointSelectorVariableKey.set(null);
  }

  private fetchDevicePointValue(pointId: string) {
    const deviceId = this.device().id;
    if (!deviceId) return;

    this.devicesService.readPointValue$(pointId, deviceId).subscribe({
      next: values => {
        const pv = values?.[0];
        this.device.update(d => ({ ...d, pointValue: pv != null ? { value: pv.Value, time: pv.Time } : null }));
      },
      error: () => {},
    });
  }

  private fetchVariablePointValue(variableKey: string, pointId: string) {
    const deviceId = this.bindings()[variableKey]?.DeviceID;
    if (!deviceId) return;

    this.devicesService.readPointValue$(pointId, deviceId).subscribe({
      next: values => {
        const pv = values?.[0];
        this.variableUiState.update(s => ({
          ...s,
          [variableKey]: { ...s[variableKey], pointValue: pv != null ? { value: pv.Value, time: pv.Time } : null },
        }));
      },
      error: () => {
      },
    });
  }

  onCalibrationOffsetChange(variableKey: string, value: string) {
    const offset = parseFloat(value);
    this.updateTransformation(variableKey, { Offset: isNaN(offset) ? undefined : offset });
  }

  onConversionChange(variableKey: string, value: string) {
    this.updateTransformation(variableKey, { Conversion: value || undefined });
  }

  onMultiplierChange(variableKey: string, value: string) {
    const multiplier = parseFloat(value);
    this.updateTransformation(variableKey, { Multiplier: isNaN(multiplier) ? undefined : multiplier });
  }

  private updateTransformation(variableKey: string, patch: FormulaBindingSource['Transformations']) {
    this.bindings.update(b => ({
      ...b,
      [variableKey]: {
        ...b[variableKey],
        Transformations: { ...b[variableKey]?.Transformations, ...patch },
      },
    }));
  }

  isBindingComplete(key: string): boolean {
    const b = this.bindings()[key];
    return !!(b?.DeviceID && b?.PointID);
  }

  onInsertText(text: string, cursorBack = 0): void {
    this.formulaInput?.insertAtCursor(text, cursorBack);
  }

  deleteVariable(variableKey: string): void {
    this.kebabOpenKey.set(null);

    // Build updated expression with the variable removed
    const newExpr = this.customFormula().expression
      .replace(new RegExp(`\\b${variableKey}\\b`, 'g'), '')
      .replace(/\s{2,}/g, ' ')
      .trim();

    this.formulaInput?.setFormula(newExpr);
  }

  next() {
    this.loading.set(true);

    const d = this.device();
    const obs$ = this.dataSource() === TileDataSource.Device
      ? this.creatorService.createDeviceTile$(
          d.pointName ?? 'New Tile',
          d.id!,
          d.pointId!,
          this.devicesService.sessUserDevices[d.id!]?.Type,
        )
      : this.isCustomFormula()
        ? this.creatorService.createCustomFormulaTile$(
            this.customFormula().expression,
            this.customFormula().variables,
            this.bindings(),
          )
        : this.creatorService.createFormulaTile$(
            this.formula()!.name,
            this.formula()!,
            this.bindings(),
          );

    obs$.subscribe({
      next: tileId => {
        this.loading.set(false);
        this.created$.emit(tileId);
      },
      error: (err: Error) => {
        this.loading.set(false);
        this.shadeTools.errorDialog(err?.message ?? 'Something went wrong. Please try again.');
      },
    });
  }

  private getDevice(deviceId: string | null): Device | null {
    return deviceId ? this.devicesService.sessUserDevices[deviceId] ?? null : null;
  }

  private resetDeviceState(): void {
    this.device.set({ id: null, name: null, pointId: null, pointName: null, pointValue: null });
    this.deviceSelectorOpen.set(false);
  }

  private resetFormulaState(): void {
    this.bindings.set({});
    this.variableUiState.set({});
    this.activeSelectorKey.set(null);
    this.formulaDropdownOpen.set(false);
    this.pointSelectorDevice.set(null);
    this.pointSelectorVariableKey.set(null);
    this.customFormula.set(EMPTY_CUSTOM_FORMULA);
  }

}
